import React, {useContext, useEffect, useRef} from "react";
import useSound from "use-sound";
import {EngageAppContext} from "../../App";
import {TriviaRunning, triviaStateDuration, TriviaStatus} from "../../state/state";
import {InputType} from "../../state/questions";
import {TimeUnit, useCountdown} from "../../hooks/useCountdown";

const MusicVolume = 0.15;
const SFXVolume = 0.4;

export default function Sounds() {
	const {appState, userState} = useContext(EngageAppContext.current);

	const countdown = useCountdown(appState.common.timeMark, triviaStateDuration(appState.common), appState.common.pauseMark, TimeUnit.SECONDS);
	//countdown is sometimes 0 or even 1 for a few frames into a new state.
	const last3Seconds = countdown === 3;

	const [playLoop, {stop: stopLoop, sound: loopSound}] = useSound("/assets/sounds/common/background-loop.mp3",{
		volume: MusicVolume,
	});
	const [playIntro, {stop: stopIntro, sound: introSound}] = useSound("/assets/sounds/common/background-intro.mp3",{
		volume: MusicVolume,
		sprite: {
			intro: [0,10000] //using a sprite seems to help to seamlessly transition to the next audio
		}
	});

	const [playQSubmit] = useSound("/assets/sounds/common/question-submit.mp3",{
		volume: SFXVolume,
	});
	const [playQTicking] = useSound("/assets/sounds/common/question-ticking.mp3",{
		volume: SFXVolume,
	});
	const [playQOutOfTime] = useSound("/assets/sounds/common/question-outoftime.mp3",{
		volume: SFXVolume,
	});
	const [playQCorrect] = useSound("/assets/sounds/common/question-correct.mp3",{
		volume: SFXVolume,
	});
	const [playQIncorrect] = useSound("/assets/sounds/common/question-incorrect.mp3",{
		volume: SFXVolume - 0.1,
	});

	// Effect to manage intro sound...
	useEffect(() => {
		if(appState.common.gameOptions.backgroundMusic) {
			if(introSound !== null) {
				const listener = () => {
					playLoop();
				};
				introSound.on("end", listener);
				return () => {
					stopIntro();
					introSound.off("end", listener);
				};
			}
		}
	}, [introSound, appState.common.gameOptions.backgroundMusic, playLoop, stopIntro]);

	// Effect to manage the loop
	useEffect(() => {
		if(appState.common.gameOptions.backgroundMusic) {
			if (loopSound !== null) {
				const listener = () => {
					playLoop();
				};
				loopSound.on("end", listener);
				return () => {
					stopLoop();
					loopSound.off("end", listener);
				};
			}
		}
	}, [loopSound, appState.common.gameOptions.backgroundMusic, playLoop, stopLoop]);

	// Effect to manage sound effects that play when the game state starts
	useEffect(() => {
		if(appState.common.gameOptions.backgroundMusic) {
			if (appState.common.state === TriviaStatus.Starting && introSound && !introSound.playing()) {
				stopLoop();
				playIntro({id: "intro"});
				return () => stopIntro();
			}
			if (TriviaRunning.includes(appState.common.state) && // If trivia is running
				appState.common.state !== TriviaStatus.Starting && // And we're not starting
				loopSound && !loopSound.playing()) { // And the loop audio isn't playing
				playLoop(); // Play it
			}
		}

		if(!TriviaRunning.includes(appState.common.state) || !appState.common.gameOptions.backgroundMusic) {
			stopLoop();
			stopIntro();
		}
		if(appState.common.gameOptions.soundEffects) {
			if(appState.common.state === TriviaStatus.QuestionSummary) {
				playQOutOfTime();
			}
		}
	}, [appState.common.state, appState.common.gameOptions.backgroundMusic, appState.common.gameOptions.soundEffects, introSound, loopSound, stopLoop, playIntro, stopIntro, playLoop, playQOutOfTime]);

	// Effect to manage sound effects that play in the last 3 seconds of a game state
	const lastPlayedStartTicksIndex = useRef<number>(-1);
	const lastPlayedSubmitTicksIndex = useRef<number>(-1);
	useEffect(() => {
		if(appState.common.gameOptions.soundEffects) {
			if (appState.common.state === TriviaStatus.QuestionStart && last3Seconds
				&& appState.common.currentIndex > lastPlayedStartTicksIndex.current) {
				playQTicking();
				lastPlayedStartTicksIndex.current = appState.common.currentIndex;
			}
			if (appState.common.state === TriviaStatus.QuestionSubmit && last3Seconds
				&& appState.common.currentIndex > lastPlayedSubmitTicksIndex.current) {
				playQTicking();
				lastPlayedSubmitTicksIndex.current = appState.common.currentIndex;
			}
		}
	}, [appState.common.currentIndex, appState.common.gameOptions.soundEffects, appState.common.state, last3Seconds, playQTicking]);

	//Effect to manage sound effects for correct/incorrect player responses
	const lastPlayedSoundQuestionIndex = useRef<number>(-1);
	useEffect(()=>{
		if(appState.common.gameOptions.soundEffects) {
			if (appState.common.state === TriviaStatus.QuestionAnswer) {
				if (userState?.responses && appState.common.currentQuestion) {
					const response = userState.responses[appState.common.currentIndex]?.value;
					if (typeof response !== "undefined" && lastPlayedSoundQuestionIndex.current < appState.common.currentIndex) {
						switch (appState.common.currentQuestion.inputType) {
							case InputType.MC:
								if (response === appState.common.currentQuestion.correctIndex) {
									playQCorrect();
								} else {
									playQIncorrect();
								}
								break;
							default:
							//TODO	Need to handle other input types
						}
						lastPlayedSoundQuestionIndex.current = appState.common.currentIndex;
					}
				}
			}
		}
	}, [appState.common.currentIndex, appState.common.currentQuestion, appState.common.gameOptions.soundEffects,
		appState.common.state, playQCorrect, playQIncorrect, userState]);

	const lastSubmitTime = useRef<number|undefined>();
	useEffect(()=>{
		if(appState.common.gameOptions.soundEffects) {
			if (appState.common.state === TriviaStatus.QuestionSubmit) {
				if (userState?.responses && appState.common.currentQuestion) {
					const response = userState.responses[appState.common.currentIndex];
					if (typeof response !== "undefined" && response.timeElapsed !== lastSubmitTime.current) {
						if (appState.common.currentQuestion.inputType === InputType.Phrase && response.score > 0) {
							playQCorrect();
						} else {
							playQSubmit();
						}
						lastSubmitTime.current = response.timeElapsed;
					}
				}
			} else if (appState.common.state === TriviaStatus.QuestionStart) {
				lastSubmitTime.current = undefined;
			}
		}
	},[appState.common.currentIndex, appState.common.currentQuestion, appState.common.gameOptions.soundEffects,
		appState.common.state, playQCorrect, playQSubmit, userState]);

	return <></>;
}

